<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BRANCH')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="branchRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="cachedBranchList ? cachedBranchList.data : branchList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(operationStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BRANCH')"
              @click="onSelectBranchItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectBranchItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_BRANCH' : 'ACTIVATE_BRANCH')"
              @click="branchStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('OPEN_CLOSE_BRANCH') && data.item.status === 'Active'"
              @click="branchOpenCloseStatus(data.item, $event.target)"
            >
              <feather-icon
                icon="CheckCircleIcon"
              />
              <span class="align-middle ml-50">{{ data.item.operationStatus === 'Open' ? $t('Close') : $t('Open') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="branchRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="name"
                v-model="branchFormData.name"
                :label="$t('Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Name')"
                rules="required"
                for="name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->

            <!-- Code -->
            <b-col md="6">
              <InputField
                id="code"
                v-model="branchFormData.code"
                :label="$t('Code')"
                :placeholder="$t('Enter code')"
                type="number"
                :error-name="$t('Code')"
                rules="required|length:3|integer"
                for="code"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Region -->
            <b-col
              md="6"
            >
              <SelectField
                id="region"
                v-model="branchFormData.regionID"
                :label="$t('Region')"
                :placeholder="$t('Select')"
                :options="regionOptions"
                :reduce="option => option.value"
                :error-name="$t('Region')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Region -->
            <!-- Address -->
            <b-col md="6">
              <InputField
                id="addres"
                v-model="branchFormData.address"
                :label="$t('Branch Address')"
                :placeholder="$t('Enter address')"
                type="text"
                :error-name="$t('Branch Address')"
                rules="required"
                for="address"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Address -->
            <!-- Contact Person Name -->
            <b-col
              md="6"
            >
              <SelectField
                id="region"
                v-model="branchFormData.contactPersonID"
                :label="$t('Contact Person Name')"
                :placeholder="$t('Select')"
                :options="contactPersonOptions"
                :reduce="option => option.value"
                :error-name="$t('Contact Person Name')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setUserPhoneNumber"
              />
            </b-col>
            <!-- Contact Person Name -->

            <!-- Contact Person Number -->
            <b-col md="6">
              <InputField
                id="addres"
                v-model="branchFormData.contactPersonNumber"
                :label="$t('Contact Person Number')"
                :placeholder="$t('Enter number')"
                type="text"
                :error-name="$t('Contact Person Number')"
                rules="required"
                for="number"
                readonly
              />
            </b-col>
            <!-- Contact Person Number -->
          </b-row>
          <b-row v-if="isViewOnly">
            <b-col
              md="12"
              class="mb-2 mt-2"
            >
              <h4>
                {{ $t('Branch Snapshot') }}
              </h4>
            </b-col>
            <b-col md="6">
              <InputField
                id="no-pending-approvals-today"
                v-model="branchSnapshot.pendingApproval"
                :label="$t('Nos. of pending approvals as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-created-casa-today"
                v-model="branchSnapshot.createdCASA"
                :label="$t('Nos. of newly created CASA as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-created-loans-today"
                v-model="branchSnapshot.createdLoans"
                :label="$t('Nos. of newly created Loans as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-created-fixed-deposit-today"
                v-model="branchSnapshot.createdFixedDeposit"
                :label="$t('Nos. of newly created Fixed Deposits as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-fd-maturing-today"
                v-model="branchSnapshot.fdMaturing"
                :label="$t('Nos. of Fixed Deposits maturing as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-cheque-clearing-today"
                v-model="branchSnapshot.chequeClearing"
                :label="$t('Nos. of Cheque clearing as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="no-loans-fallen-due-today"
                v-model="branchSnapshot.loansFallenDue"
                :label="$t('Nos. of Loans fallen due as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="net-vault-account-today"
                v-model="branchSnapshot.vaultAccount"
                :label="$t('Net balance of Vault account activities as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="net-till-account-today"
                v-model="branchSnapshot.tillAccount"
                :label="$t('Net balance of Till account activities as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="net-cash-suspense-account-today"
                v-model="branchSnapshot.cashSuspenseAccount"
                :label="$t('Net balance of cash suspense account activities as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="net-overage-account-today"
                v-model="branchSnapshot.cashOverageAccount"
                :label="$t('Net balance of cash overage account activities as at today')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="net-shortage-account-today"
                v-model="branchSnapshot.cashShortageAccount"
                :label="$t('Net balance of cash shortage account activities as at today')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchBranch"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->

        <!-- Open/Close Status -->
        <b-col md="6">
          <SelectField
            id="open-close-status"
            v-model="filter.operationStatus"
            :label="$t('Open/Close Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedOpenCloseStatusOption"
            item="label"
          />
        </b-col>
        <!-- Open/Close Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BForm, BDropdownItem, BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import { operationPermissions } from '../../../helpers/permissions'

// import { modalBus } from '../../../main'

const openCloseStatusOption = [
  { label: 'Opened', value: 'Opened' },
  { label: 'Closed', value: 'Closed' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BBadge,
    BSpinner,
    SelectField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Button,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isViewOnly: false,
      isReadonly: false,
      isEditable: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'name',
      required,
      modalTitle: this.$t('Create Branch'),
      sortDesc: false,
      showSpinner: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'regionName', sortable: true, label: 'Region' },
        { key: 'contactPersonName', sortable: true, label: 'Contact Name' },
        { key: 'contactPersonNumber', sortable: true, label: "Contact's No." },
        { key: 'operationStatus', sortable: true, label: 'Open/Close' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      branchFormData: {
        name: '',
        code: '',
        address: '',
        regionID: '',
        regionName: '',
        contactPersonID: '',
        contactPersonName: '',
        contactPersonNumber: '',
        id: '',
      },
      filter: {
        name: '',
        code: '',
        status: '',
        operationStatus: '',
        sortColumn: '',
      },
      branchList: [],
      regionOptions: [],
      contactPersonOptions: [],
      branchSnapshot: {
        pendingApproval: '',
        createdCASA: '',
        createdLoans: '',
        createdFixedDeposit: '',
        fdMaturing: '',
        chequeClearing: '',
        loansFallenDue: '',
        vaultAccount: '',
        tillAccount: '',
        cashSuspenseAccount: '',
        cashOverageAccount: '',
        cashShortageAccount: '',
      },

    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedOpenCloseStatusOption() {
      return openCloseStatusOption.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedBranchList() {
      return this.getTableCache('branch', this.cacheKey)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchBranch()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchBranch()
    },
  },
  mounted() {
    this.serveCacheAndFetchBranch()
    this.getRegionOptions()
    this.getContactPersonOptions()
  },

  methods: {
    ...mapActions({
      getRegions: 'operations/getAllRegions',
      getUsers: 'operations/getAllUsers',
      getBranch: 'operations/getBranchList',
      updateBranch: 'operations/updateBranchAction',
      createBranch: 'operations/createBranchAction',
      updateBranchStatus: 'operations/updateStatus',
      updateBranchOpenCloseStatus: 'operations/updateOpenCloseStatus',
      getUserPhoneNumber: 'operations/getUserPhoneNumberById',
      getAllBranches: 'operations/getAllBranches',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),
    async onSelectBranchItem(items, activity, val) {
      if (val === 'view') {
        this.isViewOnly = true
      }
      this.isEditable = true
      this.branchFormData.name = items.name
      this.branchFormData.code = items.code
      this.branchFormData.address = items.address
      this.branchFormData.regionID = items.regionID
      this.branchFormData.regionName = items.regionName
      this.branchFormData.contactPersonID = items.contactPersonID
      this.branchFormData.contactPersonName = items.contactPersonName
      this.branchFormData.contactPersonNumber = items.contactPersonNumber
      this.branchFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Branch') : this.$t('Branch')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async serveCacheAndFetchBranch() {
      // Serve cache if available
      const { cacheKey } = this
      const cache = this.getTableCache('branch', cacheKey)
      if (cache && cache.data) {
        this.branchList = cache.data
        this.totalCount = cache.totalCount
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchBranch(false)
      }
    },
    async fetchBranch(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        const response = await this.getBranch({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_BRANCH,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          // If data is different from cache, update UI
          const cache = this.cachedBranchList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.branchList = data.data
            this.totalCount = data.totalItems
            // If on page 1 and data changed, mark all branch cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('branch')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'branch',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.totalItems,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    async branchAction() {
      const requestText = this.$t('Your request to')
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createBranch(this.branchFormData)
          : await this.updateBranch(this.branchFormData)
        const { status } = await response
        if (status === 200) {
          this.markTableCacheStale('branch')
          this.fetchBranch(true)
          this.getAllBranches({ permission: operationPermissions.VIEW_BRANCH })
          this.hideModal('modal-lg')
          const successText = this.$t('Branch was successful')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Branch failed. Reason:')
          this.alertError({ text: `${requestText} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.branchRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Branch?')
          this.confirm(this.branchAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
        }
      })
    },
    async branchStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_BRANCH' : 'ACTIVATE_BRANCH'
      const yourRequest = this.$t('Your request to')
      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateBranchStatus({
            statusValues, actionType, routeName: 'Branch', permission,
          })
          if (response.status === 200) {
            this.markTableCacheStale('branch')
            this.fetchBranch(true)
            this.getAllBranches({ permission: operationPermissions.VIEW_BRANCH })
            const successText = this.$t('Branch was successful')
            this.alertSuccess({ text: `${yourRequest} ${action} ${items.name} ${successText}` })
            this.showSpinner = false
          }
        }
        if (actionType === 'InActive' && items.operationStatus === 'Open') {
          const reasontext = this.$t('Branch failed. Reason: Branch status is currently opened')
          this.alertError({ text: `${yourRequest} ${action} ${reasontext}` })
        } else {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Branch?')
          this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
        }
      } catch (error) {
        const reasonText = this.$t('Branch failed. Reason:')
        this.alertError({ text: `${yourRequest} ${action} ${reasonText} ${error}` })
      } finally {
        this.showSpinner = false
        this.isLoading = false
      }
    },
    async branchOpenCloseStatus(items) {
      const action = items.operationStatus === 'Open' ? this.$t('Closed') : this.$t('Open')
      const actionState = action === 'Open' ? 1 : 2
      try {
        const updateOpenCloseStatus = async () => {
          this.showSpinner = true
          const formData = {
            id: items.id,
            code: items.code,
            operationStatus: actionState,
          }
          const response = await this.updateBranchOpenCloseStatus({ formData, permission: operationPermissions.OPEN_CLOSE_BRANCH })
          if (response.status === 200) {
            this.markTableCacheStale('branch')
            this.fetchBranch(true)
            this.alertSuccess({ text: `Your request to ${action} ${items.name} Branch was successful` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const thisText = this.$t('this Branch?')
        this.confirm(updateOpenCloseStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
      } catch (error) {
        const yourRequest = this.$t('Your request to')
        const reasonText = this.$t('Branch failed. Reason:')
        this.alertError({ text: `${yourRequest} ${action} ${reasonText} ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    resetModal() {
      this.branchFormData.name = null
      this.branchFormData.code = null
      this.branchFormData.address = null
      this.branchFormData.regionID = null
      this.branchFormData.contactPersonID = null
      this.branchFormData.contactPersonNumber = null
      this.branchFormData.id = null
      this.isReadonly = false
      this.isLoading = false
      this.isEditable = false
      this.isViewOnly = false
      this.modalTitle = this.$t('Create Branch')
      this.buttonLabel = this.$t('Save')
    },
    async getRegionOptions() {
      try {
        const response = await this.getRegions()
        const data = await response.data
        this.regionOptions = data.data.map(item => ({ label: item.name, value: item.id }))
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getContactPersonOptions() {
      try {
        const formData = {
          status: 'Active',
        }
        const response = await this.getUsers({
          formData,
          permission: operationPermissions.VIEW_BRANCH,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.contactPersonOptions = data.data
            .map(item => ({ label: `${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.othernames ?? ''}`, value: item.id }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async setUserPhoneNumber(selectedValue) {
      try {
        const response = await this.getUserPhoneNumber(selectedValue)
        const { status, data } = await response
        if (status === 200 && data) {
          this.branchFormData.contactPersonNumber = data.phoneNumber
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    resetFilters() {
      this.filter.name = ''
      this.filter.code = ''
      this.filter.status = ''
      this.filter.operationStatus = ''
      this.filter.sortColumn = ''
      this.markTableCacheStale('branch')
      this.fetchBranch(true)
    },
    resetFilter() {
      this.filter = {}
      this.markTableCacheStale('branch')
      this.fetchBranch(true)
    },
  },
}
</script>
